import { Avatar, Toggle } from '@pa/ui'
import { MapPin } from 'lucide-react'
import { fmtTime, WEEK_ORDER } from '../model/hours'
import type { Location } from '../model/types'
import { useSettingsDraft } from '../SettingsDraftProvider'
import { PfTags } from './PfTags'
import { focusClass, useFocusRow } from './useFocusRow'

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** PF facility hours (D-31) in one line: "PF hours: Mon 8:00 AM – 5:00 PM, …"; null when PF reported none. */
function pfHoursLine(l: Location): string | null {
  if (!l.pfHours) return null
  const open = WEEK_ORDER.flatMap((d) => l.pfHours?.filter((h) => h.weekday === d && h.open) ?? [])
  if (!open.length) return 'PF hours: closed all week'
  return `PF hours: ${open.map((h) => `${SHORT_DAYS[h.weekday] ?? ''} ${fmtTime(h.start)} – ${fmtTime(h.end)}`).join(', ')}`
}

/**
 * C-07 Locations (UserFlows C-07): Show, the name with its PF tags, [New] (Q-2) the address patients see, and the
 * PF facility hours (D-31). Providers: "All providers, clinic hours" listing every shown provider (D-73: there are
 * no per-location weekdays), or "No providers shown".
 */
export function LocationsTab({
  focus,
}: {
  /** A row id from a C-05 arrow: scrolled to and flashed. */
  focus?: string | undefined
} = {}) {
  const { draft, update } = useSettingsDraft()
  useFocusRow(focus)
  const shownProviders = draft.providers.filter((p) => p.visible)
  const patch = (id: string, p: Partial<Location>) => {
    update((d) => ({ ...d, locations: d.locations.map((l) => (l.id === id ? { ...l, ...p } : l)) }))
  }
  return (
    <div className="card">
      <div className="table-wrap">
        <table className="table rtable set-table loc-table">
          <thead>
            <tr>
              <th scope="col">Show</th>
              <th scope="col">Location</th>
              <th scope="col">Providers</th>
            </tr>
          </thead>
          <tbody>
            {draft.locations.map((l) => {
              const hours = pfHoursLine(l)
              return (
                <tr
                  key={l.id}
                  data-row={l.id}
                  className={`${l.visible ? '' : 'off'} ${focusClass(l.id, focus)}`.trim()}
                >
                  <td className="c-toggle">
                    <Toggle
                      checked={l.visible}
                      onChange={(v) => {
                        patch(l.id, { visible: v })
                      }}
                      label={`Show ${l.name}`}
                    />
                  </td>
                  <td className="c-name">
                    <div className="row loc-name">
                      <MapPin aria-hidden className="loc-pin" />
                      <div className="grow">
                        <b>{l.name}</b> <PfTags removed={l.removedInPf} isNew={l.isNew} />
                        <label className="q2-label" htmlFor={`address-${l.id}`}>
                          Address
                        </label>
                        <input
                          id={`address-${l.id}`}
                          className="input q2-input"
                          value={l.address}
                          maxLength={300}
                          placeholder="Street, city, state ZIP"
                          onChange={(e) => {
                            patch(l.id, { address: e.target.value })
                          }}
                        />
                        {hours && <span className="pf-hours">{hours}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="c-prov" data-label="Providers">
                    {shownProviders.length ? (
                      <>
                        <span className="cell-sub">All providers, clinic hours</span>
                        <div className="chip-list">
                          {shownProviders.map((p) => (
                            <span key={p.id} className="mini-chip">
                              <Avatar name={p.name} color={p.color} size="sm" /> {p.name}
                            </span>
                          ))}
                        </div>
                      </>
                    ) : (
                      <span className="muted">No providers shown</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="card-foot hint">
        At least one location must be shown. Patients pick a location first, then a visit type and a provider.
      </div>
    </div>
  )
}
